import { useCallback } from 'react';
import { bookingsApi } from '../api/bookings.api.js';
import { paymentsApi } from '../api/payments.api.js';
import { useBookingStore } from '../store/booking.store.js';

export function useBooking() {
  const store = useBookingStore();
  const {
    setBookingId,
    setBookingGroupId,
    setBookingExpiresAt,
    setStripeAccountId,
    setClientSecret,
    setCustomerSessionClientSecret,
    reset,
  } = store;

  const startBooking = useCallback(
    async (
      gymId: string,
      slotStartTime: string,
      offerCode?: string,
      savePaymentMethod = false,
      useCredit = true,
      includePartner = false,
    ) => {
      const res = await bookingsApi.create(gymId, slotStartTime, offerCode, useCredit, includePartner);
      setBookingId(res.data.bookingId);
      setBookingGroupId(null);
      setBookingExpiresAt(res.data.expiresAt);

      if (!res.data.requiresPayment) {
        setStripeAccountId(null);
        setClientSecret(null);
        setCustomerSessionClientSecret(null);
        return {
          ...res.data,
          bookingId: res.data.bookingId,
          bookingExpiresAt: res.data.expiresAt,
          clientSecret: null,
          customerSessionClientSecret: null,
        };
      }

      const intentRes = await paymentsApi.createIntent(res.data.bookingId, savePaymentMethod);
      setStripeAccountId(intentRes.data.stripeAccountId);
      setClientSecret(intentRes.data.clientSecret);
      setCustomerSessionClientSecret(intentRes.data.customerSessionClientSecret);

      return {
        ...res.data,
        bookingId: res.data.bookingId,
        bookingExpiresAt: res.data.expiresAt,
        clientSecret: intentRes.data.clientSecret,
        customerSessionClientSecret: intentRes.data.customerSessionClientSecret,
      };
    },
    [setStripeAccountId, setBookingExpiresAt, setBookingGroupId, setBookingId, setClientSecret, setCustomerSessionClientSecret],
  );

  const startBookingBatch = useCallback(
    async (
      gymId: string,
      slotStartTimes: string[],
      offerCode?: string,
      savePaymentMethod = false,
      useCredit = true,
      includePartner = false,
    ) => {
      const res = await bookingsApi.createBatch(gymId, slotStartTimes, offerCode, useCredit, includePartner);
      setBookingId(res.data.bookingId);
      setBookingGroupId(res.data.bookingGroupId);
      setBookingExpiresAt(res.data.expiresAt);

      if (!res.data.requiresPayment) {
        setStripeAccountId(null);
        setClientSecret(null);
        setCustomerSessionClientSecret(null);
        return {
          ...res.data,
          bookingId: res.data.bookingId,
          bookingGroupId: res.data.bookingGroupId,
          bookingExpiresAt: res.data.expiresAt,
          clientSecret: null,
          customerSessionClientSecret: null,
        };
      }

      const intentRes = res.data.bookingGroupId
        ? await paymentsApi.createGroupIntent(res.data.bookingGroupId, savePaymentMethod)
        : await paymentsApi.createIntent(res.data.bookingId, savePaymentMethod);
      setStripeAccountId(intentRes.data.stripeAccountId);
      setClientSecret(intentRes.data.clientSecret);
      setCustomerSessionClientSecret(intentRes.data.customerSessionClientSecret);

      return {
        ...res.data,
        bookingId: res.data.bookingId,
        bookingGroupId: res.data.bookingGroupId,
        bookingExpiresAt: res.data.expiresAt,
        clientSecret: intentRes.data.clientSecret,
        customerSessionClientSecret: intentRes.data.customerSessionClientSecret,
      };
    },
    [
      setStripeAccountId,
      setBookingExpiresAt,
      setBookingGroupId,
      setBookingId,
      setClientSecret,
      setCustomerSessionClientSecret,
    ],
  );

  const replaceBooking = useCallback(
    async (
      bookingId: string,
      gymId: string,
      slotStartTime: string,
      offerCode?: string,
      savePaymentMethod = false,
      useCredit = true,
      includePartner = false,
    ) => {
      const res = await bookingsApi.replace(bookingId, gymId, slotStartTime, offerCode, useCredit, includePartner);
      setBookingId(res.data.bookingId);
      setBookingGroupId(null);
      setBookingExpiresAt(res.data.expiresAt);

      if (!res.data.requiresPayment) {
        setStripeAccountId(null);
        setClientSecret(null);
        setCustomerSessionClientSecret(null);
        return {
          ...res.data,
          bookingId: res.data.bookingId,
          bookingExpiresAt: res.data.expiresAt,
          clientSecret: null,
          customerSessionClientSecret: null,
        };
      }

      const intentRes = await paymentsApi.createIntent(res.data.bookingId, savePaymentMethod);
      setStripeAccountId(intentRes.data.stripeAccountId);
      setClientSecret(intentRes.data.clientSecret);
      setCustomerSessionClientSecret(intentRes.data.customerSessionClientSecret);

      return {
        ...res.data,
        bookingId: res.data.bookingId,
        bookingExpiresAt: res.data.expiresAt,
        clientSecret: intentRes.data.clientSecret,
        customerSessionClientSecret: intentRes.data.customerSessionClientSecret,
      };
    },
    [setStripeAccountId, setBookingExpiresAt, setBookingGroupId, setBookingId, setClientSecret, setCustomerSessionClientSecret],
  );

  const resumePayment = useCallback(
    async (
      bookingId: string,
      bookingGroupId: string | null,
      savePaymentMethod = false,
    ) => {
      const intentRes = bookingGroupId
        ? await paymentsApi.createGroupIntent(bookingGroupId, savePaymentMethod)
        : await paymentsApi.createIntent(bookingId, savePaymentMethod);
      setStripeAccountId(intentRes.data.stripeAccountId);
      setClientSecret(intentRes.data.clientSecret);
      setCustomerSessionClientSecret(intentRes.data.customerSessionClientSecret);
      return intentRes.data;
    },
    [setStripeAccountId, setClientSecret, setCustomerSessionClientSecret],
  );

  const cancelBooking = useCallback(
    async (bookingId: string, reason?: string) => {
      await bookingsApi.cancel(bookingId, reason);
      reset();
    },
    [reset],
  );

  return {
    ...store,
    startBooking,
    startBookingBatch,
    replaceBooking,
    resumePayment,
    cancelBooking,
  };
}
